import constants from '../constants';
import { colorMarkClassName, editedElementClassName, VerticalAlignClassName } from '../../../constants';
import * as helpers from '../../helpers';
import { TextFieldsEditableData, addStylesToEditableTextFieldsData } from '../../editableTextFields/textFieldsEditableData';
import { DuplicatedItemInSpreadConfig } from '../html';
import richTextEditorHelpers from '../../../../../components/Editor/RichTextEditor/helpers';

export interface IndesignTextFieldsConfig {
	page: number;
	width: number;
	height: number;
	id: string;
	className: string;
	parentId: string | null;
	transformStyle: string;
	content: string;
	scale: number;
	selfScale: number;
	verticalAlignClassName: VerticalAlignClassName | '';
	isThemedItem: boolean;
	isEditedFromBackoffice?: boolean;
	isEmpty?: boolean;
}

export interface TextFieldsData {
	items: IndesignTextFieldsConfig[];
	duplicatedItemsInSpread: DuplicatedItemInSpreadConfig[];
	editableData: TextFieldsEditableData | undefined;
	isError: boolean;
}

const getTextElementsFromStyles = (styles: string, innerHTML: string): HTMLSpanElement[] => {
	const stylesArray = styles.split(';').filter(style => style);

	// inDesign doesn't export text-decoration style. But it is needed for proper work of <RichTextEditor> component
	if (!styles.includes('text-decoration')) {
		stylesArray.push('text-decoration:none');
	}

	return stylesArray.map((style, index, array) => {
		const span = document.createElement('span');

		// A special class is needed for proper color of underline (managed in css)
		if (style.includes('color')) {
			span.classList.add(colorMarkClassName);
		}

		span.setAttribute('style', style);
		const isLast = index === array.length - 1;
		if (isLast) {
			span.innerHTML = innerHTML;
		}
		return span;
	});
};

const replaceTextElements = (oldElement: HTMLSpanElement, newElements: HTMLSpanElement[]): void => {
	for (let i = 1; i < newElements.length; i += 1) {
		newElements[i - 1].appendChild(newElements[i]);
	}

	oldElement.parentNode!.replaceChild(newElements[0], oldElement);
};

const replaceTextElementsAccessible = (
	originalElement: HTMLElement,
	newSpans: HTMLSpanElement[]
): void => {
	// Create a clone of the original element to preserve its tag and attributes
	const newElement = originalElement.cloneNode(false) as HTMLElement;

	const sanitizeSpan = (span: HTMLSpanElement): HTMLSpanElement | null => {
		const hasContent = span.textContent && span.textContent.trim() !== '';
		const hasStyles = span.style.cssText && span.style.cssText.trim() !== '';

		if (!hasContent || !hasStyles) {
			return null;
		}

		const newSpan = document.createElement('span');

		// Copy only the necessary styles
		if (span.style.textDecoration !== 'none') {
			newSpan.style.textDecoration = span.style.textDecoration;
		}
		newSpan.innerHTML = span.innerHTML;
		return newSpan;
	};

	newSpans.forEach(span => {
		const sanitizedSpan = sanitizeSpan(span);

		if (sanitizedSpan) {
			newElement.appendChild(sanitizedSpan);
		}
	});

	if (originalElement.parentNode) {
		originalElement.parentNode.replaceChild(newElement, originalElement);
	}
};

const getModifiedParagraphElementsStyle = (elements: HTMLParagraphElement[], scale: number): void => {
	elements.forEach((element) => {
		const styles = element.getAttribute('style');

		if (styles) {
			let modifiedStyles = helpers.getStylesStringWithoutSpaces(styles);
			modifiedStyles = helpers.getStylesStringWithoutLastSemicolon(modifiedStyles);
			modifiedStyles = helpers.getStylesWithScaledNumbers(modifiedStyles, scale);
			element.setAttribute('style', modifiedStyles);
		}

		return element;
	});
};

const getModifiedTextElementsContent = (elements: HTMLSpanElement[], scale: number): void => {
	elements.forEach((element) => {
		const styles = element.getAttribute('style');

		if (styles) {
			const modifiedStyles = helpers.getStylesWithScaledNumbers(styles, scale);

			// By default Slate adds line-break symbol to each paragraph of external pasted text. It should be removed for proper display
			const innerHTML = element.innerHTML.replace(/\n/g, '');
			const textElements = getTextElementsFromStyles(modifiedStyles, innerHTML);

			if (helpers.isTextScriptElement(element.className)) {
				const textScriptSpan = document.createElement('span');
				textScriptSpan.className = helpers.getTextScriptClassName(element.className);
				const lastTextElement = textElements[textElements.length - 1];
				const content = lastTextElement.innerHTML;
				textScriptSpan.innerHTML = content;
				lastTextElement.innerHTML = '';
				textElements.push(textScriptSpan);
			}

			replaceTextElements(element, textElements);
		}
	});
};

const getTextElementsStyles = (textElements: HTMLSpanElement[], css: string, scale: number, editableData: TextFieldsEditableData | undefined): { [k: string]: string } => (
	textElements.reduce((acc, current) => {
		const { className, classList } = current;
		// generated css for text elements has selectors like span.CharOverride-1 but text elements can have several classes. We have to remove another classes from selector
		const filteredClassName = [...classList].find(className => (
			className.includes(constants.textFieldCharacterClassName)
		));

		if (acc[className]) {
			return acc;
		}

		const styles = helpers.getSelectorStylesFromCss(`\\.${filteredClassName}`, css);

		if (styles) {
			const modifiedStyles = helpers.getStylesWithScaledNumbers(styles, scale);

			if (editableData) {
				addStylesToEditableTextFieldsData(editableData, modifiedStyles);
			}

			acc[className] = modifiedStyles;
		}

		return acc;
	}, {})
);

const getTextElementInlineStyles = (textElement: HTMLSpanElement, scale: number, editableData: TextFieldsEditableData | undefined): string => {
	const stylesToCheck = ['letter-spacing'];
	const styles = stylesToCheck
		.reduce((acc, current) => {
			const { style } = textElement;
			if (style[current]) {
				acc.push(`${current}:${style[current]};`);
			}
			return acc;
		}, [] as string[])
		.map((style) => {
			if (style.includes('letter-spacing')) {
				const value = style.split(':')[1];
				const correctedValue = helpers.getCorrectedLetterSpacing(value, scale);
				return correctedValue ? `letter-spacing:${correctedValue}px;` : '';
			}
			return style;
		})
		.join('');

	if (editableData) {
		addStylesToEditableTextFieldsData(editableData, styles);
	}

	return styles;
};

const getUnmodifiedParagraphElementsStyle = (
	parentClassName: string,
	elements: HTMLParagraphElement[],
	css: string,
	scale: number,
	editableData: TextFieldsEditableData | undefined,
	isAccessible: boolean,
): void => {
	elements.forEach((element, index) => {
		// remove all inDesign classes to avoid styles collision
		if (!isAccessible) {
			element.removeAttribute('class');
		}

		const textFieldClassNameRegExp = new RegExp(`${constants.textFieldClassName}-\\d+_\\d+`);
		const match = parentClassName.match(textFieldClassNameRegExp);
		const textFieldClassName = match && match[0];

		if (!textFieldClassName) {
			return;
		}

		const selector = `\\[class\\*=\\"${textFieldClassName}\\"\\] p:nth-of-type\\(${index + 1}\\)`;
		const styles = helpers.getSelectorStylesFromCss(selector, css);

		if (styles) {
			let modifiedStyles = helpers.getStylesStringWithoutLastSemicolon(styles);
			modifiedStyles = helpers.getStylesWithScaledNumbers(modifiedStyles, scale);

			if (editableData) {
				addStylesToEditableTextFieldsData(editableData, modifiedStyles);
			}

			element.setAttribute('style', modifiedStyles);
		}
	});
};

const getUnmodifiedTextElementsContent = (
	elements: HTMLSpanElement[],
	css: string,
	scale: number,
	editableData: TextFieldsEditableData | undefined,
	isAccessible: boolean,
): void => {
	const textElementsStyles = getTextElementsStyles(elements, css, scale, editableData);
	let textElementInnerHtml = '';
	let isReplacementNeeded = true;

	elements.forEach((element, index, elements) => {
		const currentElement = element;
		const nextElement = elements[index + 1];

		const currentElementInlineStyles = getTextElementInlineStyles(
			currentElement,
			scale,
			editableData,
		);
		const currentElementStyles = `${textElementsStyles[currentElement.className]}${currentElementInlineStyles}`;

		let elementToReplace = currentElement;

		if (nextElement) {
			const nextElementInlineStyles = getTextElementInlineStyles(
				nextElement,
				scale,
				editableData,
			);
			const nextElementStyles = `${textElementsStyles[nextElement.className]}${nextElementInlineStyles}`;
			const isSiblings = currentElement.parentNode === nextElement.parentNode;
			const isSameStyles = currentElementStyles === nextElementStyles;

			if (isSiblings && isSameStyles) {
				isReplacementNeeded = false;
				currentElement.parentNode!.removeChild(currentElement);
				elementToReplace = nextElement;
			} else {
				isReplacementNeeded = true;
			}
		} else {
			isReplacementNeeded = true;
		}

		textElementInnerHtml += currentElement.innerHTML;

		if (isReplacementNeeded) {
			const newElements = getTextElementsFromStyles(currentElementStyles, textElementInnerHtml);

			if (isAccessible) {
				replaceTextElementsAccessible(elementToReplace, newElements);
			} else {
				replaceTextElements(elementToReplace, newElements);
			}

			textElementInnerHtml = '';
		}
	});
};

const getContent = (
	parentClassName: string,
	element: HTMLDivElement,
	css: string,
	scale: number,
	isEditedFromBackoffice: boolean,
	editableData: TextFieldsEditableData | undefined,
	isAccessible: boolean,
): string => {
	const paragraphElements = isAccessible
		? [
			...element.querySelectorAll('#page-container'),
		] as HTMLParagraphElement[]
		: [
			...element.querySelectorAll('p, li'),
		] as HTMLParagraphElement[];

	const textElements = isAccessible
		? [
			...element.querySelectorAll('h1, h2, h3, h4, h5, h6, p'),
		] as HTMLParagraphElement[]
		: [
			...element.querySelectorAll('p span, li span'),
		] as HTMLSpanElement[];

	if (isEditedFromBackoffice && !isAccessible) {
		getModifiedParagraphElementsStyle(paragraphElements, scale);
		getModifiedTextElementsContent(textElements, scale);
	} else {
		getUnmodifiedParagraphElementsStyle(
			parentClassName,
			paragraphElements,
			css,
			scale,
			editableData,
			isAccessible,
		);
		getUnmodifiedTextElementsContent(textElements, css, scale, editableData, isAccessible);
	}

	let content = isEditedFromBackoffice || isAccessible
		? element.innerHTML
		// We have to remove unnecessary spaces generated by inDesign for proper render in <RichTextEditor />
		: helpers.getStringWithoutSpaces(element.innerHTML);

	// tslint:disable-next-line: max-line-length
	// is needed to have the same html string in store and <RichTextEditor /> inner state in order to prevent issues when text field was focused and then blurred without any modification:
	// 1) unexpected saving of page with the text field;
	// 2) creation of instance in history state.
	const deserializedContent = richTextEditorHelpers.html.deserialize(content);
	const serializedContent = richTextEditorHelpers.html.serialize(deserializedContent);

	return serializedContent;
};

// in some cases (text frame inside animated frame) we have to change some styles of text frame parent element. To achieve it we have to know parent id
const getParentId = (element: HTMLElement): string | null => {
	const elementWrapperSelector = `[id*=${constants.parentContainerId}]:not([class*=${constants.textFieldClassName}]) > div[class*=${constants.textFieldWrapperClassName}]`;
	const elementWrapper = element.closest(elementWrapperSelector);
	const elementWrapperParent = elementWrapper && elementWrapper.parentNode as HTMLElement;

	return elementWrapperParent ? elementWrapperParent.id : null;
};

const getConfig = (
	element: HTMLElement,
	page: number,
	pageIndexInSpread: number,
	css: string,
	editableData: TextFieldsEditableData | undefined,
	isAccessible: boolean,
): IndesignTextFieldsConfig | DuplicatedItemInSpreadConfig | undefined => {
	const { id, className, classList } = element;
	const innerDiv = element.querySelector('div') as HTMLDivElement;

	if (!innerDiv) {
		return;
	}

	const filteredClassName = className.split(' ').find(className => (
		className.includes(constants.textFieldClassName)
	)) || '';

	if (helpers.isDuplicatedItemInSpread(filteredClassName, pageIndexInSpread)) {
		return {
			page,
			id,
			className: filteredClassName,
		};
	}

	const style = innerDiv.style || {};
	const scale = helpers.getScaleFromStyle(style) || 1;

	let width = style.width ? helpers.getNumberFromString(style.width as string) * scale : undefined;
	let height = style.height ? helpers.getNumberFromString(style.height as string) * scale : undefined;

	width = width || 0;
	height = height || 0;

	const isEditedFromBackoffice = classList.contains(editedElementClassName);

	return {
		page,
		width: width,
		height: height,
		id,
		className: filteredClassName,
		parentId: getParentId(element),
		transformStyle: helpers.getTransformStyle(style),
		content: getContent(
			className,
			innerDiv,
			css,
			scale,
			isEditedFromBackoffice,
			editableData,
			isAccessible,
		),
		scale,
		// some text fields have transform: scale property. We have to take it into account to calculate correct outline width of active text frame
		selfScale: helpers.getElementScale(element, css),
		verticalAlignClassName: helpers.getVerticalAlignClassName(className),
		isThemedItem: className.includes(constants.themedItemClassName),
	};
};

export default (page: number, pageIndexInSpread: number, htmlDocument: Document, css: string, isModifiedFromBackoffice: boolean, isAccessible: boolean = false): TextFieldsData => {
	const items: IndesignTextFieldsConfig[] = [];
	const duplicatedItemsInSpread: DuplicatedItemInSpreadConfig[] = [];
	const editableData = isModifiedFromBackoffice
		? undefined
		: {
			fontFamilies: new Set<string>(),
			fontSizes: new Set<string>(),
			colors: new Set<string>(),
			lineHeights: new Set<string>(),
			letterSpacings: new Set<string>(),
		};
	const editableTextFieldElements = ([
		...htmlDocument.querySelectorAll(`[class*=${constants.textFieldClassName}]:not([class*=${constants.lockedClassName}])`),
	] as HTMLElement[]).filter(element => !helpers.hasLockedParent(element));

	let isError: boolean = false;

	try {
		editableTextFieldElements.forEach((fieldElement) => {
			const config = getConfig(fieldElement, page, pageIndexInSpread, css, editableData, isAccessible);

			if (config) {
				if ('content' in config) {
					items.push(config);
				} else {
					duplicatedItemsInSpread.push(config);
				}
			}
		});
	} catch (err) {
		isError = true;
	}

	return {
		items,
		duplicatedItemsInSpread,
		editableData,
		isError,
	};
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/indesignEditor/editableTextFields/getData.ts